import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { boardAPI, listAPI, recipeAPI } from '../services/api';
import Board from '../components/Board';
import RecipeModal from '../components/RecipeModal';
import ImportModal from '../components/ImportModal';
import AISearchModal from '../components/AISearchModal';

function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [currentBoard, setCurrentBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importListId, setImportListId] = useState(null);
  const [showAISearch, setShowAISearch] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    loadBoards();
  }, []);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchResults]);

  const loadBoards = async () => {
    try {
      setLoading(true);
      const response = await boardAPI.getAll();
      console.log('Boards response:', response.data);
      if (response.data && response.data.boards) {
        setBoards(response.data.boards);
        if (response.data.boards.length > 0) {
          setCurrentBoard(response.data.boards[0]);
        } else {
          setCurrentBoard(null);
        }
      } else {
        console.error('Unexpected response format:', response.data);
        setBoards([]);
        setCurrentBoard(null);
      }
    } catch (error) {
      console.error('Failed to load boards:', error);
      setBoards([]);
      setCurrentBoard(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setShowSearchResults(false);
      setSearchResults([]);
      return;
    }

    try {
      const response = await recipeAPI.search(searchQuery);
      setSearchResults(response.data.recipes || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  const handleAddList = async () => {
    const title = prompt('Enter list name:');
    if (!title) return;

    try {
      await listAPI.create(currentBoard.id, title);
      await loadBoards();
    } catch (error) {
      console.error('Failed to create list:', error);
    }
  };

  const handleAddRecipe = (listId) => {
    setImportListId(listId);
    setShowImport(true);
  };

  const handleRecipeClick = (recipe) => {
    // Navigate to recipe detail page
    navigate(`/recipe/${recipe.id}`);
  };

  const handleRecipeSave = async () => {
    await loadBoards();
    setSelectedRecipe(null);
  };

  const handleImportComplete = async () => {
    await loadBoards();
    setShowImport(false);
    setImportListId(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="loading-spinner" style={{ width: '40px', height: '40px', borderWidth: '4px' }}></div>
      </div>
    );
  }

  return (
    <>
      <header className="header" style={{ position: 'relative' }}>
        <div className="header-content">
          <Link to="/" className="header-logo" style={{ textDecoration: 'none' }}>
            <img src="/logo.png" alt="RecipeWorks" className="header-logo-img" onError={(e) => { e.target.style.display = 'none'; }} />
          </Link>
          <div className="header-nav" ref={searchRef}>
            <form onSubmit={handleSearch} style={{ position: 'relative' }}>
              <input
                type="search"
                className="header-search"
                placeholder="Search recipes..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (!e.target.value.trim()) {
                    setShowSearchResults(false);
                    setSearchResults([]);
                  }
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#666'
                  }}
                >
                  ×
                </button>
              )}
            </form>
            {showSearchResults && (
              <div className="search-results" style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                right: '0',
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                maxHeight: '400px',
                overflowY: 'auto',
                zIndex: 1000,
                marginTop: '4px'
              }}>
                {searchResults.length > 0 ? (
                  <div>
                    <div style={{ padding: '8px 12px', borderBottom: '1px solid #eee', fontWeight: 'bold', fontSize: '12px', color: '#666' }}>
                      Found {searchResults.length} recipe{searchResults.length !== 1 ? 's' : ''}
                    </div>
                    {searchResults.map(recipe => (
                      <div
                        key={recipe.id}
                        onClick={() => {
                          navigate(`/recipe/${recipe.id}`);
                          setShowSearchResults(false);
                        }}
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #eee',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}
                      >
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{recipe.title}</div>
                        {recipe.description && (
                          <div style={{ fontSize: '12px', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {recipe.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                    No recipes found
                  </div>
                )}
              </div>
            )}
            <span>{user.username}</span>
            <button className="header-button" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard">
        <div className="dashboard-header">
          <h1>{currentBoard?.title || 'My Recipes'}</h1>
          <div className="board-selector">
            <button
              type="button"
              className="btn btn-primary btn-board-action"
              onClick={() => setShowAISearch(true)}
              title="Search your recipes or the web with AI"
            >
              Ask AI for recipes
            </button>
            <Link to="/create-from-ingredients" className="btn btn-primary btn-board-action">
              Create a recipe for me
            </Link>
            <select
              value={currentBoard?.id || ''}
              onChange={(e) => {
                const board = boards.find(b => b.id === parseInt(e.target.value));
                setCurrentBoard(board);
              }}
            >
              {boards.map(board => (
                <option key={board.id} value={board.id}>{board.title}</option>
              ))}
            </select>
            <button type="button" className="btn btn-primary btn-board-action" onClick={handleAddList}>
              + Add List
            </button>
          </div>
        </div>

        <Board
          board={currentBoard}
          onAddRecipe={handleAddRecipe}
          onRecipeClick={handleRecipeClick}
          onUpdate={loadBoards}
        />
      </div>

      {selectedRecipe && (
        <RecipeModal
          recipe={selectedRecipe}
          onClose={() => setSelectedRecipe(null)}
          onSave={handleRecipeSave}
          onDelete={async () => {
            await recipeAPI.delete(selectedRecipe.id);
            setSelectedRecipe(null);
            await loadBoards();
          }}
        />
      )}

      {showImport && (
        <ImportModal
          listId={importListId}
          onClose={() => setShowImport(false)}
          onImport={handleImportComplete}
        />
      )}

      {showAISearch && (
        <AISearchModal
          boards={boards}
          onClose={() => setShowAISearch(false)}
          onImportComplete={loadBoards}
        />
      )}
    </>
  );
}

export default Dashboard;
